// src/components/StatisticsView.tsx
import React, { useState, useMemo, useRef } from 'react';
import { 
  Activity, 
  Target, 
  Shield, 
  GitMerge, 
  TrendingUp, 
  Calendar,
  Users,
  MapPin,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Trophy,
  X,
  Check
} from 'lucide-react';

// Types voor het detail scherm
type DetailCategory = 'customer' | 'club' | 'string' | 'racket' | 'tension' | 'hybrid' | 'profit' | null;

// Filter en Navigatie typen
type FilterType = 'last30' | 'thisMonth' | 'prevMonth' | 'q' | 'year' | 'month' | 'week';
type FilterData = { type: FilterType; year?: number; q?: number; month?: number; week?: number; label: string };
type NavLevel = { level: 'main' | 'year' | 'quarters' | 'months' | 'weeks'; year?: number; fromMain?: boolean };
type NavItem = { label: string; isNavOnly?: boolean; filter?: FilterData; action?: () => void; };

interface StatisticsViewProps {
  jobs?: any[];
  customers?: any[]; 
  onMergeClubs?: (primaryClub: string, secondaryClubs: string[]) => void; // NIEUW
}

// Helpers voor datum en weken
const getStartOfWeek = (year: number, week: number, startDay: 'monday' | 'sunday') => {
  const d = new Date(year, 0, 1);
  const day = d.getDay(); 
  const diff = startDay === 'monday' ? (day === 0 ? -6 : 1 - day) : -day;
  d.setDate(d.getDate() + diff); 
  d.setDate(d.getDate() + (week - 1) * 7); 
  return d;
};

const getIsoWeekNumber = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

export default function StatisticsView({ jobs = [], customers = [], onMergeClubs }: StatisticsViewProps) {
  // Bereken huidige week voor de default state
  const nu = new Date();
  const huidigJaar = nu.getFullYear();
  const huidigeWeek = getIsoWeekNumber(nu);

  // --- STATE ---
  const [activeFilter, setActiveFilter] = useState<FilterData>({ type: 'week', year: huidigJaar, week: huidigeWeek, label: 'Deze week' });
  const [navLevel, setNavLevel] = useState<NavLevel>({ level: 'main' });
  const [activeDetail, setActiveDetail] = useState<DetailCategory>(null);
  
  const [weekStartDay, setWeekStartDay] = useState<'monday' | 'sunday'>('monday');
  const [tournamentNames, setTournamentNames] = useState<Record<string, string>>({});
  
  // NIEUW: State voor de Club Samenvoegen Modal
  const [showClubMergeModal, setShowClubMergeModal] = useState(false);
  const [primaryClub, setPrimaryClub] = useState<string>('');
  const [secondaryClubs, setSecondaryClubs] = useState<Set<string>>(new Set());

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollTimeline = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const amount = direction === 'left' ? -250 : 250;
      scrollContainerRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  // Haal alle unieke clubs op uit het klantenbestand voor de samenvoeg-lijst
  const allUniqueClubs = useMemo(() => {
      const clubs = new Set<string>();
      customers.forEach(c => {
          if (c.club && c.club.trim() !== '') clubs.add(c.club.trim());
      });
      return Array.from(clubs).sort((a, b) => a.localeCompare(b));
  }, [customers]);

  const handleToggleSecondaryClub = (club: string) => {
      const newSet = new Set(secondaryClubs);
      if (newSet.has(club)) newSet.delete(club);
      else newSet.add(club);
      setSecondaryClubs(newSet);
  };

  const handleConfirmClubMerge = () => {
      if (!primaryClub || secondaryClubs.size === 0) {
          alert("Kies een hoofdclub én minimaal één club om samen te voegen.");
          return;
      }
      if (onMergeClubs) {
          onMergeClubs(primaryClub, Array.from(secondaryClubs));
          setShowClubMergeModal(false);
          setPrimaryClub('');
          setSecondaryClubs(new Set());
      } else {
          alert("De samenvoeg-functie is nog niet doorgegeven vanuit de hoofdpagina (App.tsx).");
      }
  };

  // --- DYNAMISCHE TIJDLIJN BOUWEN ---
  const timelineItems = useMemo(() => {
    const availableYears = Array.from(new Set(jobs.map(j => {
      if (!j.date) return null;
      return new Date(j.date).getFullYear();
    }).filter(Boolean) as number[])).sort((a,b)=>b-a);

    if (availableYears.indexOf(huidigJaar) === -1) {
      availableYears.unshift(huidigJaar);
    }

    const items: NavItem[] = [];

    if (navLevel.level === 'main') {
      const thisWeekKey = `${huidigJaar}-W${huidigeWeek}`;
      const thisWeekTournament = tournamentNames[thisWeekKey];
      const thisWeekLabel = thisWeekTournament ? `Deze week (${thisWeekTournament})` : 'Deze week';
      
      items.push({ 
        label: thisWeekLabel, 
        filter: { type: 'week', year: huidigJaar, week: huidigeWeek, label: 'Deze week' } 
      });
      
      items.push({ label: 'Laatste 30 dagen', filter: { type: 'last30', label: 'Laatste 30 dagen' } });
      items.push({ label: 'Deze maand', filter: { type: 'thisMonth', label: 'Deze maand' } });
      
      items.push({ label: 'Weken', isNavOnly: true, action: () => setNavLevel({ level: 'weeks', year: huidigJaar, fromMain: true }) });
      items.push({ label: 'Maanden', isNavOnly: true, action: () => setNavLevel({ level: 'months', year: huidigJaar, fromMain: true }) });
      items.push({ label: 'Kwartalen', isNavOnly: true, action: () => setNavLevel({ level: 'quarters', year: huidigJaar, fromMain: true }) });

      availableYears.forEach(year => {
        items.push({
          label: `${year}`,
          filter: { type: 'year', year, label: `Heel ${year}` },
          action: () => setNavLevel({ level: 'year', year })
        });
      });
      
    } else if (navLevel.level === 'year') {
      items.push({ label: '⬅ Terug', isNavOnly: true, action: () => setNavLevel({ level: 'main' }) });
      items.push({ label: `Heel ${navLevel.year}`, filter: { type: 'year', year: navLevel.year, label: `Heel ${navLevel.year}` } });
      items.push({ label: 'Weken', isNavOnly: true, action: () => setNavLevel({ ...navLevel, level: 'weeks' }) });
      items.push({ label: 'Maanden', isNavOnly: true, action: () => setNavLevel({ ...navLevel, level: 'months' }) });
      items.push({ label: 'Kwartalen', isNavOnly: true, action: () => setNavLevel({ ...navLevel, level: 'quarters' }) });
      
    } else if (navLevel.level === 'weeks') {
      const goBack = () => navLevel.fromMain ? setNavLevel({ level: 'main' }) : setNavLevel({ level: 'year', year: navLevel.year });
      items.push({ label: '⬅ Terug', isNavOnly: true, action: goBack });
      
      let maxWeek = 52;
      if (navLevel.year === huidigJaar) {
        maxWeek = huidigeWeek;
      } else if (navLevel.year && navLevel.year > huidigJaar) {
        maxWeek = 0; 
      }
      
      for (let w = maxWeek; w >= 1; w--) {
        const wKey = `${navLevel.year}-W${w}`;
        const tName = tournamentNames[wKey];
        
        const isThisWeek = (navLevel.year === huidigJaar && w === huidigeWeek);
        const baseLabel = isThisWeek ? 'Deze week' : `Week ${w}`;
        const displayLabel = tName ? `${baseLabel} (${tName})` : baseLabel;

        items.push({ 
          label: displayLabel, 
          filter: { type: 'week', year: navLevel.year, week: w, label: displayLabel } 
        });
      }

    } else if (navLevel.level === 'quarters' || navLevel.level === 'months') {
      const goBack = () => navLevel.fromMain ? setNavLevel({ level: 'main' }) : setNavLevel({ level: 'year', year: navLevel.year });
      items.push({ label: '⬅ Terug', isNavOnly: true, action: goBack });
      
      if (navLevel.level === 'quarters') {
        [1, 2, 3, 4].forEach(q => {
          items.push({ label: `Q${q}`, filter: { type: 'q', year: navLevel.year, q, label: `Q${q} ${navLevel.year}` } });
        });
      } else {
        const months = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
        months.forEach((m, idx) => {
          items.push({ label: m, filter: { type: 'month', year: navLevel.year, month: idx, label: `${m} ${navLevel.year}` } });
        });
      }
    }

    return items;
  }, [jobs, navLevel, tournamentNames, huidigJaar, huidigeWeek]);

  const handleNavClick = (item: NavItem) => {
    if (item.filter) {
      setActiveFilter(item.filter);
      setActiveDetail(null);
    }
    if (item.action) {
      item.action();
    }
  };

  let displayLabel = activeFilter.label;
  if (activeFilter.type === 'week' && activeFilter.year && activeFilter.week) {
    const isThisWeek = activeFilter.year === huidigJaar && activeFilter.week === huidigeWeek;
    const tName = tournamentNames[`${activeFilter.year}-W${activeFilter.week}`];
    
    if (isThisWeek) {
      displayLabel = tName ? `Deze week (${tName})` : 'Deze week';
    } else {
      displayLabel = tName ? `Week ${activeFilter.week} (${tName}) - ${activeFilter.year}` : `Week ${activeFilter.week} (${activeFilter.year})`;
    }
  }

  // --- DE REKENMOTOR ---
  const { currentData, fullLists } = useMemo(() => {
    const now = new Date();
    const f = activeFilter;
    
    const filteredJobs = jobs.filter(job => {
      if (!job.date) return false; 
      const jobDate = new Date(job.date);
      
      if (f.type === 'last30') {
          return (now.getTime() - jobDate.getTime()) <= 30 * 24 * 60 * 60 * 1000;
      }
      if (f.type === 'thisMonth') {
          return jobDate.getMonth() === now.getMonth() && jobDate.getFullYear() === now.getFullYear();
      }
      if (f.type === 'prevMonth') {
          let prevM = now.getMonth() - 1;
          let prevY = now.getFullYear();
          if (prevM < 0) { prevM = 11; prevY--; }
          return jobDate.getMonth() === prevM && jobDate.getFullYear() === prevY;
      }
      if (f.type === 'year') {
          return jobDate.getFullYear() === f.year;
      }
      if (f.type === 'q' && f.q && f.year) {
          const qStartMonth = (f.q - 1) * 3;
          return jobDate.getFullYear() === f.year && jobDate.getMonth() >= qStartMonth && jobDate.getMonth() <= qStartMonth + 2;
      }
      if (f.type === 'month' && f.month !== undefined && f.year) {
          return jobDate.getFullYear() === f.year && jobDate.getMonth() === f.month;
      }
      if (f.type === 'week' && f.week !== undefined && f.year !== undefined) {
          const start = getStartOfWeek(f.year, f.week, weekStartDay);
          const end = new Date(start);
          end.setDate(end.getDate() + 7);
          return jobDate.getTime() >= start.getTime() && jobDate.getTime() < end.getTime();
      }
      return true;
    });

    const getFullList = (arr: string[]) => {
      const total = arr.length || 1; 
      const counts = arr.reduce((acc: Record<string, number>, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {});
      
      return Object.keys(counts)
        .map(key => ({
            name: key,
            count: counts[key],
            percentage: Math.round((counts[key] / total) * 100)
        }))
        .sort((a, b) => b.count - a.count);
    };

    const strings = filteredJobs.map(j => j.string).filter(Boolean);
    const tensions = filteredJobs.map(j => `${j.tensionMain || 24}/${j.tensionCross || j.tensionMain || 24} kg`);
    const rackets = filteredJobs.map(j => {
        if (typeof j.racket === 'string' && j.racket.trim() !== '') return j.racket;
        if (j.racketBrand || j.racketModel) return `${j.racketBrand || ''} ${j.racketModel || ''}`.trim();
        return null;
    }).filter(Boolean) as string[];
    const hybrids = filteredJobs.filter(j => j.isHybrid).map(j => `${j.string || '?'} / ${j.crossString || '?'}`);

    const customerNames: string[] = [];
    const clubs: string[] = [];
    const profitPerString: Record<string, number> = {};

    filteredJobs.forEach(job => {
      const customer = customers.find(c => c.id === job.customerId);
      if (customer) {
          if (customer.name) customerNames.push(customer.name);
          if (customer.club) clubs.push(customer.club);
      }
      if (job.string && job.price) {
        profitPerString[job.string] = (profitPerString[job.string] || 0) + (parseFloat(job.price) || 0);
      }
    });

    const fullStrings = getFullList(strings);
    const fullTensions = getFullList(tensions);
    const fullRackets = getFullList(rackets);
    const fullHybrids = getFullList(hybrids);
    const fullCustomers = getFullList(customerNames);
    const fullClubs = getFullList(clubs);

    const profitValues = Object.keys(profitPerString).map(key => profitPerString[key]);
    const totalProfit = profitValues.reduce((a: number, b: number) => a + b, 0) || 1;
    const fullProfits = Object.keys(profitPerString)
      .map(key => ({
          name: key,
          amount: profitPerString[key],
          percentage: Math.round((profitPerString[key] / totalProfit) * 100)
      }))
      .sort((a, b) => b.amount - a.amount);

    const formatPrice = (num: number) => num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const currentData = {
      totalJobs: filteredJobs.length,
      topString: fullStrings[0]?.name || '-',
      topStringSub: filteredJobs.length > 0 && fullStrings[0] ? `${fullStrings[0].count} keer gebruikt` : 'Geen data',
      topTension: fullTensions[0]?.name || '-',
      topTensionSub: filteredJobs.length > 0 && fullTensions[0] ? `${fullTensions[0].percentage}% van alle rackets` : '0 opdrachten',
      topRacket: fullRackets[0]?.name || '-',
      topRacketSub: filteredJobs.length > 0 && fullRackets[0] ? `${fullRackets[0].count}x bespannen` : 'Geen data',
      topHybrid: fullHybrids[0]?.name || 'Geen hybrides gebruikt',
      topHybridSub: fullHybrids.length > 0 && fullHybrids[0] ? `${fullHybrids[0].count}x gekozen` : '-',
      topCustomer: fullCustomers[0]?.name || '-',
      topCustomerSub: fullCustomers.length > 0 && fullCustomers[0] ? `${fullCustomers[0].count} opdrachten` : 'Geen data',
      topClub: fullClubs[0]?.name || '-',
      topClubSub: fullClubs.length > 0 && fullClubs[0] ? `${fullClubs[0].count} rackets uit deze club` : 'Geen data',
      topProfitString: fullProfits[0]?.name || '-',
      topProfitAmount: `€ ${formatPrice(fullProfits[0]?.amount || 0)} omzet`
    };

    return { 
      currentData, 
      fullLists: { string: fullStrings, tension: fullTensions, racket: fullRackets, hybrid: fullHybrids, customer: fullCustomers, club: fullClubs, profit: fullProfits }
    };
  }, [jobs, customers, activeFilter, weekStartDay]);

  const formatPrice = (num: number) => num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const renderDetailView = () => {
    if (!activeDetail) return null;

    let title = ''; let icon = null; let list: any[] = []; let isCurrency = false; let labelSuffix = '';

    switch (activeDetail) {
      case 'customer': title = 'Alle Klanten'; icon = <Users className="w-6 h-6 text-pink-500" />; list = fullLists.customer; labelSuffix = 'opdrachten'; break;
      case 'club': title = 'Alle Tennisclubs'; icon = <MapPin className="w-6 h-6 text-cyan-500" />; list = fullLists.club; labelSuffix = 'rackets'; break;
      case 'string': title = 'Alle Snaren'; icon = <Activity className="w-6 h-6 text-indigo-500" />; list = fullLists.string; labelSuffix = 'keer'; break;
      case 'racket': title = 'Alle Rackets'; icon = <Shield className="w-6 h-6 text-blue-500" />; list = fullLists.racket; labelSuffix = 'keer'; break;
      case 'tension': title = "Alle Kilo's"; icon = <Target className="w-6 h-6 text-emerald-500" />; list = fullLists.tension; labelSuffix = 'keer'; break;
      case 'hybrid': title = 'Alle Hybride Combinaties'; icon = <GitMerge className="w-6 h-6 text-purple-500" />; list = fullLists.hybrid; labelSuffix = 'keer'; break;
      case 'profit': title = 'Omzet per Snaar'; icon = <TrendingUp className="w-6 h-6 text-amber-500" />; list = fullLists.profit; isCurrency = true; break;
    }

    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center">
              <button onClick={() => setActiveDetail(null)} className="p-2 mr-4 bg-slate-100 dark:bg-slate-700 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600 transition text-slate-600 dark:text-slate-300">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                {icon}
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white">{title}</h2>
              </div>
          </div>
          
          {/* NIEUW: Knop om dubbele clubs samen te voegen */}
          {activeDetail === 'club' && (
              <button 
                  onClick={() => setShowClubMergeModal(true)}
                  className="flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition font-medium text-sm border border-indigo-100 dark:border-indigo-800"
              >
                  <GitMerge className="w-4 h-4 mr-2" />
                  Samenvoegen
              </button>
          )}
        </div>

        <div className="space-y-6">
          {list.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-10">Geen data beschikbaar in <span className="font-bold">{displayLabel}</span>.</p>
          ) : (
            list.map((item, index) => (
              <div key={index} className="flex flex-col gap-2">
                <div className="flex justify-between items-end">
                  <span className="font-bold text-slate-800 dark:text-white text-lg">{item.name}</span>
                  <div className="text-right">
                    <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                      {isCurrency ? `€ ${formatPrice(item.amount)}` : `${item.count} ${labelSuffix}`}
                    </span>
                    <span className="ml-3 text-indigo-600 dark:text-indigo-400 font-bold w-12 inline-block text-right">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-700/50 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 dark:bg-indigo-500 rounded-full transition-all duration-1000 ease-out" style={{ width: `${item.percentage}%` }}></div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6 md:p-8 relative">
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
        
        {/* Header & Filters */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-indigo-500" />
              Statistieken & Inzichten
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Data weergave: <span className="font-bold text-indigo-600 dark:text-indigo-400">{displayLabel}</span> ({currentData.totalJobs} rackets)
            </p>
            
            {activeFilter.type === 'week' && activeFilter.year && activeFilter.week && (
              <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 animate-in slide-in-from-top-2">
                <div className="inline-flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 shadow-sm">
                  <span className="text-xs font-medium text-slate-500 pl-2">Start toernooi:</span>
                  <button 
                    onClick={() => setWeekStartDay('monday')}
                    className={`ml-2 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${weekStartDay === 'monday' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'}`}
                  >
                    Maandag
                  </button>
                  <button 
                    onClick={() => setWeekStartDay('sunday')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${weekStartDay === 'sunday' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'}`}
                  >
                    Zondag
                  </button>
                </div>

                <div className="inline-flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1 shadow-sm px-2">
                  <Trophy className="w-4 h-4 text-slate-400 mr-2" />
                  <input 
                    type="text"
                    placeholder="Toernooinaam toevoegen..."
                    value={tournamentNames[`${activeFilter.year}-W${activeFilter.week}`] || ''}
                    onChange={(e) => setTournamentNames(prev => ({ ...prev, [`${activeFilter.year}-W${activeFilter.week}`]: e.target.value }))}
                    className="px-2 py-1 text-sm bg-transparent border-none focus:ring-0 outline-none text-slate-700 dark:text-slate-200 placeholder:text-slate-400 w-48 sm:w-64"
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="w-full lg:w-auto relative group flex items-center">
            <button onClick={() => scrollTimeline('left')} className="absolute left-0 z-10 p-1.5 bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700 rounded-full text-slate-500 hover:text-indigo-600 -ml-4 opacity-0 md:group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center">
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div ref={scrollContainerRef} className="flex items-center gap-1.5 overflow-x-auto hide-scrollbar scroll-smooth p-1.5 w-full lg:max-w-xl border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-200/50 dark:bg-slate-800/50">
              <div className="flex items-center pl-2 pr-1 text-slate-400 dark:text-slate-500 shrink-0">
                <Calendar className="w-4 h-4" />
              </div>
              
              {timelineItems.map((item, idx) => {
                const f = item.filter;
                const isSelected = !item.isNavOnly && f && 
                  activeFilter.type === f.type && 
                  activeFilter.year === f.year && 
                  activeFilter.month === f.month && 
                  activeFilter.q === f.q && 
                  activeFilter.week === f.week;

                return (
                  <button
                    key={idx}
                    onClick={() => handleNavClick(item)}
                    className={`
                      whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shrink-0
                      ${isSelected 
                        ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm border border-slate-200/50 dark:border-slate-600' 
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 border border-transparent'}
                    `}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>

            <button onClick={() => scrollTimeline('right')} className="absolute right-0 z-10 p-1.5 bg-white dark:bg-slate-800 shadow-md border border-slate-200 dark:border-slate-700 rounded-full text-slate-500 hover:text-indigo-600 -mr-4 opacity-0 md:group-hover:opacity-100 transition-opacity hidden md:flex items-center justify-center">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Dashboard Grid / Detail Weergave */}
        {activeDetail ? renderDetailView() : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 animate-in fade-in duration-300">            
            <div onClick={() => setActiveDetail('customer')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 group-hover:scale-110 transition-transform"><Users className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Meest actieve<br/>klant</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topCustomer}>{currentData.topCustomer}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topCustomerSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('club')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform"><MapPin className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Populairste<br/>tennisclub</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topClub}>{currentData.topClub}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topClubSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('string')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform"><Activity className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Meest gebruikte<br/>snaar</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topString}>{currentData.topString}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topStringSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('racket')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform"><Shield className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Meest bespannen<br/>racket</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topRacket}>{currentData.topRacket}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topRacketSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('tension')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform"><Target className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Meest gekozen<br/>aantal kilo's</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topTension}>{currentData.topTension}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topTensionSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('hybrid')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform"><GitMerge className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Meest gekozen<br/>hybride combi</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topHybrid}>{currentData.topHybrid}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentData.topHybridSub}</p>
              </div>
            </div>

            <div onClick={() => setActiveDetail('profit')} className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 shadow-sm flex flex-col transition-all hover:shadow-md hover:ring-2 hover:ring-indigo-500/50 cursor-pointer group">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform"><TrendingUp className="w-6 h-6" /></div>
                <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 leading-tight">Hoogste omzet<br/>(per snaar)</h3>
              </div>
              <div className="mt-auto">
                <p className="text-lg font-bold text-slate-900 dark:text-white truncate" title={currentData.topProfitString}>{currentData.topProfitString}</p>
                <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mt-1">{currentData.topProfitAmount}</p>
              </div>
            </div>

          </div>
        )}

        {/* MODAL: CLUBS SAMENVOEGEN */}
        {showClubMergeModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-xl w-full max-w-2xl border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh]">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-slate-800 dark:text-white flex items-center">
                            <GitMerge className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                            Clubs Samenvoegen
                        </h3>
                        <button onClick={() => setShowClubMergeModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    
                    <div className="mb-6">
                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">1. Kies de juiste naam (Hoofdclub)</label>
                        <select 
                            value={primaryClub} 
                            onChange={(e) => {
                                setPrimaryClub(e.target.value);
                                // Zorg dat de gekozen hoofdclub niet óók als secundaire club geselecteerd blijft staan
                                if (secondaryClubs.has(e.target.value)) {
                                    const newSet = new Set(secondaryClubs);
                                    newSet.delete(e.target.value);
                                    setSecondaryClubs(newSet);
                                }
                            }}
                            className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white outline-none focus:border-indigo-500"
                        >
                            <option value="">-- Selecteer Hoofdclub --</option>
                            {allUniqueClubs.map(club => (
                                <option key={`primary-${club}`} value={club}>{club}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex-1 overflow-y-auto mb-6 pr-2">
                        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">2. Welke foutieve namen wil je hierin samenvoegen?</label>
                        <div className="space-y-2">
                            {allUniqueClubs.filter(club => club !== primaryClub).map(club => (
                                <div 
                                    key={`secondary-${club}`} 
                                    onClick={() => handleToggleSecondaryClub(club)}
                                    className={`p-3 rounded-xl border-2 cursor-pointer transition flex items-center justify-between ${secondaryClubs.has(club) ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700'}`}
                                >
                                    <span className="font-medium text-slate-800 dark:text-slate-200">{club}</span>
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${secondaryClubs.has(club) ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {secondaryClubs.has(club) && <Check className="w-3 h-3" />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700 mt-auto">
                        <button 
                            onClick={() => setShowClubMergeModal(false)}
                            className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition font-medium"
                        >
                            Annuleren
                        </button>
                        <button 
                            onClick={handleConfirmClubMerge}
                            disabled={!primaryClub || secondaryClubs.size === 0}
                            className={`px-6 py-2 rounded-xl font-bold transition flex items-center shadow-sm ${(!primaryClub || secondaryClubs.size === 0) ? 'bg-slate-300 text-slate-500 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                        >
                            Nu Samenvoegen
                        </button>
                    </div>
                </div>
            </div>
        )}

      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}